import { useEffect, useState } from 'react';
import type { PersonasConfig } from '../api/uiSignoff';

// Apps and personas for UI sign-off, kept in the portal so the client repo needs no
// config/ui-signoff.json. The API checks the result with the extractor's own validator, so what
// saves here is exactly what the CLI would accept.

const field: React.CSSProperties = { display: 'grid', gap: 4, fontSize: 13 };
const input: React.CSSProperties = { border: '1px solid var(--mi-border)', borderRadius: 6, padding: '6px 8px', font: 'inherit', fontSize: 13, minWidth: 0 };
const secondary: React.CSSProperties = { background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', color: 'var(--mi-text)', padding: '5px 10px', fontSize: 13 };
const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

/**
 * A comma-separated list that keeps what was typed. Re-rendering from the parsed list would eat a
 * trailing comma, so a second value could never be typed.
 */
export function ListInput({ value, onChange, ...rest }: { value: string[]; onChange(v: string[]): void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [text, setText] = useState(value.join(', '));
  useEffect(() => { if (list(text).join('|') !== value.join('|')) setText(value.join(', ')); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return <input {...rest} style={{ ...input, ...(rest.style ?? {}) }} value={text} onChange={(e) => { setText(e.target.value); onChange(list(e.target.value)); }} />;
}

export const EMPTY_PERSONAS: PersonasConfig = { apps: [], personas: [{ id: '', label: '', profile: '', permissionSets: [] }] };

/** A pasted config/ui-signoff.json, reduced to the shape the portal stores. Null if it isn't one. */
export function fromRepoJson(text: string): PersonasConfig | null {
  try {
    const raw = JSON.parse(text) as Partial<PersonasConfig>;
    if (!Array.isArray(raw.apps) || !Array.isArray(raw.personas)) return null;
    return {
      apps: raw.apps.map(String),
      personas: raw.personas.map((p) => ({
        id: String(p.id ?? ''), label: String(p.label ?? ''), profile: String(p.profile ?? ''),
        permissionSets: Array.isArray(p.permissionSets) ? p.permissionSets.map(String) : [],
        ...(Array.isArray(p.apps) && p.apps.length ? { apps: p.apps.map(String) } : {}),
      })),
      ...(raw.core?.namespace ? { core: { namespace: String(raw.core.namespace), ...(raw.core.dependency ? { dependency: String(raw.core.dependency) } : {}) } } : {}),
    };
  } catch {
    return null;
  }
}

interface Props {
  value: PersonasConfig | null;
  onChange(v: PersonasConfig | null): void;
}

export default function PersonasEditor({ value, onChange }: Props) {
  const [paste, setPaste] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);
  const inPortal = value !== null;
  const v = value ?? EMPTY_PERSONAS;
  const setPersona = (i: number, patch: Partial<PersonasConfig['personas'][number]>) =>
    onChange({ ...v, personas: v.personas.map((p, j) => (j === i ? { ...p, ...patch } : p)) });

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="radio" name="personas-source" checked={inPortal} onChange={() => onChange(value ?? EMPTY_PERSONAS)} />
          Kept here in the portal
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="radio" name="personas-source" checked={!inPortal} onChange={() => onChange(null)} />
          Read from the repo’s <code>config/ui-signoff.json</code>
        </label>
      </div>

      {!inPortal && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--mi-muted)' }}>
          The repo must contain <code>config/ui-signoff.json</code> on every branch reviewers open. Switching to “kept here” means the repo needs no file.
        </p>
      )}

      {inPortal && (
        <>
          <label style={field}>Apps, in priority order (developer names, comma-separated). When two apps give a persona different pages, the first wins.
            <ListInput aria-label="Apps" value={v.apps} onChange={(apps) => onChange({ ...v, apps })} placeholder="Acme_Marketing, Acme_Admin" />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <label style={field}>Core namespace
              <input aria-label="Core namespace" style={input} value={v.core?.namespace ?? ''} placeholder="clientverse"
                onChange={(e) => { const ns = e.target.value.trim(); onChange({ ...v, core: ns ? { ...v.core, namespace: ns } : undefined }); }} />
            </label>
            <label style={field}>Core package name in sfdx-project.json (optional)
              <input aria-label="Core dependency" style={input} value={v.core?.dependency ?? ''} disabled={!v.core?.namespace} placeholder="ClientVerse Core"
                onChange={(e) => v.core && onChange({ ...v, core: { namespace: v.core.namespace, ...(e.target.value.trim() ? { dependency: e.target.value.trim() } : {}) } })} />
            </label>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Personas</div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--mi-muted)' }}>
              A persona is a profile plus permission sets or permission set groups; together they decide which fields it sees. Name profiles as Salesforce shows them (e.g. “BD & Marketing User”), not as their file names. Have the client confirm each one. Changing a persona later marks its views “Changed since signed”.
            </p>
            {v.personas.map((p, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr 1.2fr 2fr 1.2fr auto', gap: 6, alignItems: 'end' }}>
                <label style={field}>ID<input aria-label={`Persona ${i + 1} id`} style={input} value={p.id} onChange={(e) => setPersona(i, { id: e.target.value.trim() })} placeholder="marketing" /></label>
                <label style={field}>Label<input aria-label={`Persona ${i + 1} label`} style={input} value={p.label} onChange={(e) => setPersona(i, { label: e.target.value })} placeholder="Marketing" /></label>
                <label style={field}>Profile<input aria-label={`Persona ${i + 1} profile`} style={input} value={p.profile} onChange={(e) => setPersona(i, { profile: e.target.value })} placeholder="Acme Marketing User" /></label>
                <label style={field}>Permission sets or groups (comma-separated)<ListInput aria-label={`Persona ${i + 1} permission sets`} value={p.permissionSets} onChange={(permissionSets) => setPersona(i, { permissionSets })} /></label>
                <label style={field}>Only these apps (optional)<ListInput aria-label={`Persona ${i + 1} apps`} value={p.apps ?? []}
                  onChange={(apps) => { const { apps: _unused, ...rest } = p; onChange({ ...v, personas: v.personas.map((x, j) => (j === i ? (apps.length ? { ...rest, apps } : rest) : x)) }); }} /></label>
                <button type="button" style={secondary} disabled={v.personas.length === 1} onClick={() => onChange({ ...v, personas: v.personas.filter((_, j) => j !== i) })} aria-label={`Remove persona ${i + 1}`}>Remove</button>
              </div>
            ))}
            <div><button type="button" style={secondary} onClick={() => onChange({ ...v, personas: [...v.personas, { id: '', label: '', profile: '', permissionSets: [] }] })}>Add persona</button></div>
          </div>

          <details style={{ fontSize: 13 }}>
            <summary style={{ cursor: 'pointer' }}>Copy from an existing <code>config/ui-signoff.json</code></summary>
            <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
              <textarea aria-label="Pasted config" style={{ ...input, minHeight: 100, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder='{ "apps": [...], "personas": [...] }' />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button type="button" style={secondary} disabled={!paste.trim()} onClick={() => {
                  const parsed = fromRepoJson(paste);
                  if (!parsed) { setPasteError('That is not a config file with "apps" and "personas".'); return; }
                  setPasteError(null); setPaste(''); onChange(parsed);
                }}>Replace the fields above</button>
                {pasteError && <span style={{ color: '#b3261e' }}>{pasteError}</span>}
              </div>
            </div>
          </details>
        </>
      )}
    </div>
  );
}
